import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography
} from '@mui/material';

function refId(ref) {
  if (!ref) return '';
  if (typeof ref === 'object' && ref._id != null) return String(ref._id);
  return String(ref);
}

/**
 * Edit scene name, character, flow, flow2 (same fields as create dialog). Does not touch layout assets.
 */
export default function SceneMetaForm({
  open,
  onClose,
  onSubmit,
  scene,
  characters = [],
  flows = [],
  submitting = false
}) {
  const characterList = Array.isArray(characters) ? characters : [];
  const flowList = Array.isArray(flows) ? flows : [];

  const [name, setName] = useState('');
  const [characterId, setCharacterId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [flow2Id, setFlow2Id] = useState('');
  const [flow3Id, setFlow3Id] = useState('');

  useEffect(() => {
    if (!open || !scene) return;
    setName((scene.name || '').trim());
    setCharacterId(refId(scene.characterId));
    setFlowId(refId(scene.flowId));
    setFlow2Id(refId(scene.flow2Id));
    setFlow3Id(refId(scene.flow3Id));
  }, [open, scene]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !scene?._id) return;
    onSubmit({
      _id: scene._id,
      name: name.trim(),
      characterId: characterId || null,
      flowId: flowId || null,
      flow2Id: flow2Id || null,
      flow3Id: flow3Id || null
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit scene</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Name, character, and flow links. Background and layout stay on the scene editor screen.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Scene name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Character (optional)"
                value={characterId}
                onChange={(e) => setCharacterId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {characterList.map((c) => (
                  <MenuItem key={c._id} value={String(c._id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Flow (optional)"
                value={flowId}
                onChange={(e) => setFlowId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Second flow (optional)"
                value={flow2Id}
                onChange={(e) => setFlow2Id(e.target.value)}
                helperText="Same scene is used in-game for all linked flows"
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Third flow (optional)"
                value={flow3Id}
                onChange={(e) => setFlow3Id(e.target.value)}
                helperText="Same scene is used in-game for all linked flows"
              >
                <MenuItem value="">None</MenuItem>
                {flowList.map((f) => (
                  <MenuItem key={f._id} value={String(f._id)}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !scene}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
